import { Box, TextField, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../common/components/Button";
import { toast } from "../../common/components/Toast";
import { SPACING } from "../../common/constants/spacing";
import { saveProject } from "../api/project";
import ProjectIconUpload from "../components/ProjectIconUpload";

function CreateNewProject() {
  const { t } = useTranslation("projects");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const nameInputRef = useRef(null);

  useEffect(() => {
    if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, []);

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImageFile(file);
      setErrors({}); // Clear errors when changing image
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setErrors({});

    // Client-side validation
    const validationErrors = {};
    if (!name.trim()) {
      validationErrors.name = [t("createProject.nameRequired")];
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("name", name);
    formData.append("description", description);

    if (imageFile) {
      formData.append("icon", imageFile);
    }

    try {
      await saveProject(formData);

      setName("");
      setDescription("");
      setIcon(null);
      setPreviewImage(null);
      setImageFile(null);
      setErrors({});

      toast.success(t("createProject.success"));

      window.location.href = "/";
    } catch (error) {
      // Handle error object with field-specific errors
      if (error.errors) {
        setErrors(error.errors);

        // Show general errors as toast
        if (error.errors.general) {
          toast.error(error.errors.general.join(", "));
        }

        // Show field-specific errors as toast
        const fieldErrors = Object.entries(error.errors)
          .filter(([key]) => key !== "general")
          .map(([key, messages]) => `${key}: ${messages.join(", ")}`)
          .join("; ");

        if (fieldErrors) {
          toast.error(fieldErrors);
        }
      } else if (error.message) {
        // Handle simple error message
        toast.error(error.message);
      } else {
        toast.error(t("createProject.error"));
      }
      setLoading(false);
    }
  };

  return (
    <div>
      <Box
        sx={{
          maxWidth: 750,
          margin: "auto",
          alignItems: "center",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div className="px-5 w-100">
          <Typography
            variant="h2"
            sx={{ mt: 10, mb: SPACING.xs, fontWeight: 600 }}
          >
            {t("createProject.title")}
          </Typography>
          <Typography variant="bodyMd" color="textSecondary">
            {t("createProject.subtitle")}
          </Typography>
          <br />
          <br />

          <ProjectIconUpload
            icon={icon}
            previewImage={previewImage}
            handleImageChange={handleImageChange}
          />
          {errors.icon && (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: "block", mt: 0.5, mb: SPACING.xs }}
            >
              {errors.icon.join(", ")}
            </Typography>
          )}

          <TextField
            label={t("createProject.name")}
            variant="outlined"
            fullWidth
            margin="normal"
            name="name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (errors.name) {
                setErrors({ ...errors, name: undefined });
              }
            }}
            inputRef={nameInputRef}
            error={!!errors.name}
            helperText={errors.name ? errors.name.join(", ") : ""}
          />

          <TextField
            label={t("createProject.description")}
            variant="outlined"
            fullWidth
            margin="normal"
            multiline
            rows={4}
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              if (errors.description) {
                setErrors({ ...errors, description: undefined });
              }
            }}
            error={!!errors.description}
            helperText={errors.description ? errors.description.join(", ") : ""}
          />

          <div className="pt-4">
            <Button variant="primary" onClick={handleSubmit} loading={loading}>
              {t("createProject.save")}
            </Button>
          </div>
        </div>
      </Box>
    </div>
  );
}

export default CreateNewProject;
